import { LayoutGrid, Rows3, Search, Table2 } from "lucide-react";

import { cn } from "@/marketing/lib/utils";
import { Input } from "@/marketing/components/ui/input";

type ViewMode = "board" | "table" | "tracker";

interface BrandOption {
  brandId: string;
  name: string;
}

interface CampaignFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  ownerFilter: "me" | "all";
  onOwnerFilterChange: (value: "me" | "all") => void;
  currentUserName?: string | undefined;
  brandFilter: string;
  onBrandFilterChange: (value: string) => void;
  brandOptions: BrandOption[];
  trafficFilter: "all" | "red" | "yellow" | "green";
  onTrafficFilterChange: (value: "all" | "red" | "yellow" | "green") => void;
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
}

function ViewToggleBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-text-xs tw-font-medium tw-transition-colors",
        active
          ? "tw-bg-foreground tw-text-background"
          : "tw-bg-background tw-text-muted-foreground hover:tw-bg-accent",
      )}
    >
      {children}
    </button>
  );
}

export function CampaignFilterBar({
  search,
  onSearchChange,
  ownerFilter,
  onOwnerFilterChange,
  currentUserName,
  brandFilter,
  onBrandFilterChange,
  brandOptions,
  trafficFilter,
  onTrafficFilterChange,
  view,
  onViewChange,
}: CampaignFilterBarProps) {
  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
      {/* The tracker view carries its own status, PIC and search filters. */}
      <div hidden={view === "tracker"} className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <div className="tw-relative">
          <Search className="tw-absolute tw-left-2.5 tw-top-1/2 tw-h-3.5 tw-w-3.5 -tw-translate-y-1/2 tw-text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by campaign or brand..."
            className="tw-h-9 tw-w-[260px] tw-pl-8 tw-text-sm"
          />
        </div>
        <select
          value={ownerFilter}
          onChange={(e) => onOwnerFilterChange(e.target.value as "me" | "all")}
          className="tw-h-9 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-text-sm"
        >
          <option value="me">Me ({currentUserName ?? "Owner"})</option>
          <option value="all">All Owners</option>
        </select>
        <select
          value={brandFilter}
          onChange={(e) => onBrandFilterChange(e.target.value)}
          className="tw-h-9 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-text-sm"
        >
          <option value="">All Brands</option>
          {brandOptions.map((b) => (
            <option key={b.brandId} value={b.brandId}>
              {b.name}
            </option>
          ))}
        </select>
        <div className="tw-flex tw-items-center tw-gap-1">
          {(["all", "red", "yellow", "green"] as const).map((tl) => (
            <button
              key={tl}
              type="button"
              onClick={() => onTrafficFilterChange(tl)}
              className={cn(
                "tw-flex tw-h-7 tw-items-center tw-gap-1.5 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-2.5 tw-text-xs tw-font-medium tw-capitalize tw-text-muted-foreground tw-transition-colors",
                trafficFilter === tl && "tw-border-primary tw-bg-accent tw-text-primary",
              )}
            >
              {tl !== "all" && (
                <span
                  className={cn(
                    "tw-h-2 tw-w-2 tw-rounded-full",
                    tl === "red" && "tw-bg-red-500",
                    tl === "yellow" && "tw-bg-amber-500",
                    tl === "green" && "tw-bg-emerald-500",
                  )}
                />
              )}
              {tl}
            </button>
          ))}
        </div>
      </div>
      <div className="tw-ml-auto tw-flex tw-items-center tw-gap-2">
        <div className="tw-flex tw-h-9 tw-overflow-hidden tw-rounded-md tw-border tw-border-input">
          <ViewToggleBtn
            active={view === "board"}
            onClick={() => onViewChange("board")}
          >
            <LayoutGrid className="tw-h-3.5 tw-w-3.5" /> Board
          </ViewToggleBtn>
          <ViewToggleBtn
            active={view === "table"}
            onClick={() => onViewChange("table")}
          >
            <Rows3 className="tw-h-3.5 tw-w-3.5" /> Table
          </ViewToggleBtn>
          <ViewToggleBtn
            active={view === "tracker"}
            onClick={() => onViewChange("tracker")}
          >
            <Table2 className="tw-h-3.5 tw-w-3.5" /> Tracker
          </ViewToggleBtn>
        </div>
      </div>
    </div>
  );
}
